import {
  applyBannerSubtractKnockoutMask,
  getLineSegments,
  whenHasLayout,
} from "../utils/text-mask-geometry.js";

const RESIZE_DEBOUNCE_MS = 150;

const HERO_WHITE_CUTOUTS = [
  {
    section: ".hero-shared-bg .banner",
    cutout: ".banner__white-cutout",
    highlight: ".banner__title-highlight",
  },
  {
    section: ".hero-shared-bg .content-col-layout",
    cutout: ".content-col-layout__white-cutout",
    highlight: ".content-col-layout__title-highlight",
  },
];

function updateWhiteCutoutMask(sectionEl, cutoutEl, highlightEl) {
  const w = sectionEl.clientWidth;
  const h = sectionEl.clientHeight;
  if (w <= 0 || h <= 0) return;

  const style = getComputedStyle(highlightEl);
  const fontSizePx = parseFloat(style.fontSize) || 16;
  const mediaRect = sectionEl.getBoundingClientRect();
  const segments = getLineSegments(highlightEl, mediaRect, fontSizePx);
  if (!segments.length) return;

  applyBannerSubtractKnockoutMask(cutoutEl, segments, w, h, style);
}

/**
 * White layers with SVG subtract masks so the single hero background video shows through
 * `.banner__title-highlight` and `.content-col-layout__title-highlight` glyphs only.
 */
export function initBannerHeroVideoCutout() {
  const bindings = [];

  for (const cfg of HERO_WHITE_CUTOUTS) {
    const sectionEl = document.querySelector(cfg.section);
    if (!sectionEl) continue;
    const cutout = sectionEl.querySelector(cfg.cutout);
    const highlight = sectionEl.querySelector(cfg.highlight);
    if (!cutout || !highlight) continue;
    bindings.push({ sectionEl, cutout, highlight });
  }

  if (!bindings.length) {
    return () => {};
  }

  let maskRafId = null;
  const schedule = () => {
    if (maskRafId != null) return;
    maskRafId = requestAnimationFrame(() => {
      maskRafId = null;
      for (const { sectionEl, cutout, highlight } of bindings) {
        updateWhiteCutoutMask(sectionEl, cutout, highlight);
      }
    });
  };

  const run = () => {
    whenHasLayout(bindings[0].sectionEl, () => {
      schedule();
      requestAnimationFrame(schedule);
    });
  };

  if (document.fonts && typeof document.fonts.ready?.then === "function") {
    document.fonts.ready.then(run).catch(run);
  } else {
    run();
  }

  const ro = new ResizeObserver(() => schedule());
  for (const { sectionEl, highlight } of bindings) {
    ro.observe(sectionEl);
    ro.observe(highlight);
  }

  let resizeTimeout;
  const onWindowResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(schedule, RESIZE_DEBOUNCE_MS);
  };
  window.addEventListener("resize", onWindowResize, { passive: true });

  return () => {
    ro.disconnect();
    window.removeEventListener("resize", onWindowResize);
    if (maskRafId != null) cancelAnimationFrame(maskRafId);
    clearTimeout(resizeTimeout);
  };
}
